// src/modulos/Parametros/ProductReturnPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Coleção no Firestore
const RETURN_REASON_COLLECTION = 'parameters/data/motivo-devolucao-produto';
const ITEMS_PER_PAGE = 10;

const ProductReturnPage = () => {
    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [usageCheckLoading, setUsageCheckLoading] = useState(false);

    // Hook de Dados
    const {
        data: reasons,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(RETURN_REASON_COLLECTION, 'name', true);

    // ============================================================
    // 1. DEFINIR filteredReasons ANTES dos callbacks que o usam
    // ============================================================
    const filteredReasons = useMemo(() => {
        if (!reasons) return [];
        if (!searchTerm.trim()) return reasons;

        const searchLower = searchTerm.toLowerCase();
        return reasons.filter(reason =>
            reason.name.toLowerCase().includes(searchLower)
        );
    }, [reasons, searchTerm]);

    const totalFilteredItems = filteredReasons.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const paginatedReasons = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredReasons.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredReasons, currentPage]);

    // Reset do Formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setNameInput('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setNameInput(item.name);
        setIsModalOpen(true);
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Verificação de Uso (simplificada para demo)
    const checkIfReasonIsUsed = useCallback(async (reasonId, reasonName) => {
        setUsageCheckLoading(true);

        try {
            // Simulação de verificação - em produção, fazer consulta real ao Firestore
            await new Promise(resolve => setTimeout(resolve, 500));

            const commonUsedReasons = [
                'QUEBRA', 'AVARIA', 'DANO', 'DEFEITO',
                'VIOLADO', 'FALHA', 'ERRADO'
            ];

            const hasUsage = commonUsedReasons.some(keyword =>
                reasonName.toUpperCase().includes(keyword)
            );

            if (hasUsage) {
                return {
                    used: true,
                    message: `O motivo "${reasonName}" já foi utilizado em processos de devolução. Excluí-lo comprometerá o histórico de análises de qualidade. Recomendamos desativar o registro em vez de excluí-lo.`
                };
            }

            return { used: false };
        } catch (error) {
            console.error("Erro ao verificar uso do motivo:", error);
            return {
                used: true,
                message: "Não foi possível verificar se este motivo está em uso. Por segurança, exclua apenas motivos não utilizados."
            };
        } finally {
            setUsageCheckLoading(false);
        }
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const usageCheck = await checkIfReasonIsUsed(id, name);

            if (usageCheck.used) {
                setFormError(usageCheck.message);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setDeleteLoading(false);
                setTimeout(() => setFormError(''), 5000);
                return;
            }

            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Motivo "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const remainingItems = filteredReasons.length - 1;
                if (currentPage > 1 && remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Motivo de Devolução: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar motivo. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, checkIfReasonIsUsed, deleteDocument, filteredReasons, currentPage]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError("O nome do Motivo de Devolução é obrigatório.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: nameInput.trim(),
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Motivo editado com sucesso!' : 'Motivo adicionado com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            console.error("Erro ao salvar motivo:", error);
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, editingItem, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Novo Motivo de Devolução'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Motivo'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Motivo de Devolução <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        className={styles.input}
                        placeholder="Ex: Produto com defeito de fabricação"
                        required
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        Descreva claramente o motivo para facilitar a análise de qualidade
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar o motivo "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita e pode afetar a integridade dos dados.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Motivos de Devolução"
                actionText="Adicionar Motivo"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={true}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar motivos de devolução..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : filteredReasons.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📋'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhum motivo cadastrado'
                            }
                            message={
                                searchTerm
                                    ? `Nenhum motivo de devolução corresponde a "${searchTerm}"`
                                    : 'Clique em "Adicionar Motivo" para começar'
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Motivo de Devolução</th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedReasons.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Atualizado: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    <button
                                                        onClick={() => handleEditClick(item)}
                                                        className={styles.actionButton}
                                                        disabled={formLoading || deleteLoading}
                                                    >
                                                        Editar
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(item.id, item.name)}
                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                        disabled={formLoading || deleteLoading || usageCheckLoading}
                                                    >
                                                        {usageCheckLoading ? 'Verificando...' : 'Deletar'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="motivos"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ProductReturnPage;